import {
  type ColumnFragment,
  type ControlCardComboBoxOptionFragment,
  ControlCardComboBoxOptionsDocument,
  type ValueFragment
} from "app/gql/graphqlSchema";
import {useTranslation} from "react-i18next";
import {useEditComboBoxOption} from "app/employee/controlCard/useEditComboBoxOption";
import {useQuery} from "app/employee/controlCard/hook/useQuery";
import type {MaybeNil} from "app/utils/types";
import {isNil} from "app/utils/stdlib";
import {useEffect} from "react";
import type {AsyncOrSync} from "ts-essentials";
import type {PrimaryRowValue} from "app/employee/controlCard/primaryKey/types";
import {IdComboBox} from "app/employee/controlCard/IdComboBox";

interface Props {
  column: ColumnFragment & {__typename: "ControlCardComboBoxColumn"};
  setValues: (value: ValueFragment[]) => AsyncOrSync<void>;
  values: PrimaryRowValue[];
}

const isOptionEqualToPrimaryRow = (option: ControlCardComboBoxOptionFragment, value: PrimaryRowValue) => {
  if (value.value.__typename === "ControlCardTextValue") {
    return option.value === value.value.text;
  } else {
    return false;
  }
};

export const PrimaryRowComboBox = ({column, setValues: setControlCardValues, values}: Props) => {
  const {t} = useTranslation();
  const handleSelect = async (values: MaybeNil<ControlCardComboBoxOptionFragment[]>) => {
    if (!isNil(values) && values.length > 0) {
      await setControlCardValues(values.map((value) => ({__typename: "ControlCardTextValue", text: value.value})));
    }
  };

  const {addItem, editItem, textExtractor, keyExtractor} = useEditComboBoxOption(column.listKey);
  const {data: optionsData, refetch, fetch} = useQuery(ControlCardComboBoxOptionsDocument);

  const title = t("control-card.add-new-record-title", {title: column.title});

  useEffect(() => {
    fetch({input: {listKey: column.listKey}});
  }, [column.listKey, fetch]);

  return (
    <IdComboBox
      isEqual={isOptionEqualToPrimaryRow}
      keyExtractor={keyExtractor}
      multiselect
      onCreate={addItem}
      onEdit={editItem}
      onSelect={handleSelect}
      options={optionsData.value?.controlCardComboBoxOptions}
      refetch={refetch}
      textExtractor={textExtractor}
      title={title}
      value={values}
    />
  );
};
